'use client';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// components
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';

// types
import {
  ETextVariants,
  ETextSize,
} from '@/components/atoms/at-text/at-text.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IOrGlobalBannerProps } from './or-global-banner.types';

// utils + hooks
import { parseImageFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { useGlobalBanner } from '@/context/globalBannerContext';

export const OrGlobalBanner = ({
  children,
  cta,
  asset,
}: IOrGlobalBannerProps) => {
  const {
    isBannerVisible: showBanner,
    handleClose,
    bannerRef,
  } = useGlobalBanner();

  if (!showBanner) {
    return null;
  }

  const RenderAsset = () => {
    if ('icon' in asset[0]) {
      const { icon } = asset[0].icon;
      return (
        <span className="hidden text-2xl md:block md:text-40">
          <AtIcon icon={icon} color="var(--color-ui-fill-primary)" />
        </span>
      );
    }

    if ('image' in asset[0]) {
      const imageProps = parseImageFromCMS(asset[0].image);
      return (
        <div className="md:w-140 relative hidden h-8 w-52 overflow-hidden rounded-[1.25rem] md:block md:h-20 md:rounded-[5rem]">
          {imageProps?.src && <AtMedia {...imageProps} />}
        </div>
      );
    }
  };

  const bannerVariants = {
    hidden: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.aside
          key="banner"
          variants={bannerVariants}
          animate="visible"
          initial="hidden"
          exit="exit"
          className="w-full bg-skin-globalBanner md:flex md:items-center"
          ref={bannerRef}
        >
          <div
            id="orGlobalBanner"
            className="container-none mx-auto flex flex-col gap-x-2 gap-y-1 pb-4 pl-3 pr-2 pt-2 md:px-0 md:py-3.75"
          >
            <div className="flex flex-row items-center justify-between md:grid md:grid-cols-12">
              <div className="md:col-span-10 md:col-start-2 md:flex md:flex-row md:items-center md:justify-center md:gap-6">
                <RenderAsset />

                <AtText
                  variant={ETextVariants.DISCLAIMER}
                  size={ETextSize.SM}
                  type="p"
                  className="pr-2 text-left md:pr-0 md:text-center [&_strong]:font-bold"
                >
                  {children}
                </AtText>
                <AtClickable
                  href={cta.url.href}
                  label={cta.url.title || cta.label}
                  ariaLabel={cta.aria_label}
                  outline={cta.outline}
                  size={EClickableSize[cta.size]}
                  variant={EClickableVariants[cta.variant]}
                  target={
                    EClickableTarget[
                      cta.target as keyof typeof EClickableTarget
                    ]
                  }
                  type={cta.type}
                  className="hidden whitespace-nowrap md:block"
                  interactLocation="or-global-banner"
                />
              </div>
              <div className="flex items-end justify-start">
                <AtClickable
                  type={EClickableTypes.ICON}
                  onClick={handleClose}
                  ariaLabel="Close"
                  interactLocation="or-global-banner"
                >
                  <span className="text-32 md:text-42">
                    <AtIcon
                      icon={EIcons.CLOSE}
                      color="var(--color-ui-fill-primary)"
                    />
                  </span>
                </AtClickable>
              </div>
            </div>
            <div className="col-span-12 block md:hidden">
              <AtClickable
                href={cta.url.href}
                label={cta.url.title || cta.label}
                ariaLabel={cta.aria_label}
                outline={cta.outline}
                size={EClickableSize[cta.size]}
                variant={EClickableVariants[cta.variant]}
                target={
                  EClickableTarget[cta.target as keyof typeof EClickableTarget]
                }
                type={cta.type}
                className="block w-fit whitespace-nowrap md:hidden"
                interactLocation="or-global-banner"
              />
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
